import React from 'react'

import { cn } from '~/styled'

import { useStickyColumnsStyle } from './useStickyColumnsCss'

export const TableStickyColumnsContainer = ({
  children,
  numberOfStickyColumns = 0,
  className,
  ...restProps
}: React.PropsWithChildren<{
  numberOfStickyColumns?: number
  className?: string
}>) => {
  const [hasScroll, setHasScroll] = React.useState<boolean>(false)
  const wrapperRef = React.useRef<HTMLDivElement | null>(null)
  const { columnsStyle } = useStickyColumnsStyle({
    numberOfStickyColumns,
    wrapperRef
  })

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const newHasScroll = event.currentTarget.scrollLeft > 0
    if (newHasScroll !== hasScroll) {
      setHasScroll(newHasScroll)
    }
  }

  return (
    <div
      onScroll={handleScroll}
      // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
      role="scrollbar"
      ref={wrapperRef}
      style={columnsStyle}
      className={cn(
        'overflow-auto',
        'max-w-full',
        'relative',
        'overscroll-x-contain',
        '[&_td,&_th]:bg-inherit',
        className,
        numberOfStickyColumns >= 1 && [
          '[&_td:nth-of-type(1),&_th:nth-of-type(1)]:sticky',
          '[&_td:nth-of-type(1),&_th:nth-of-type(1)]:left-(--sticky-offset-left-1)',
          '[&_td:nth-of-type(1),&_th:nth-of-type(1)]:min-w-(--sticky-column-width-1)',
          '[&_td:nth-of-type(1),&_th:nth-of-type(1)]:z-2'
        ],
        numberOfStickyColumns >= 2 && [
          '[&_td:nth-of-type(2),&_th:nth-of-type(2)]:sticky',
          '[&_td:nth-of-type(2),&_th:nth-of-type(2)]:left-(--sticky-offset-left-2)',
          '[&_td:nth-of-type(2),&_th:nth-of-type(2)]:min-w-(--sticky-column-width-2)',
          '[&_td:nth-of-type(2),&_th:nth-of-type(2)]:z-2'
        ],
        numberOfStickyColumns >= 3 && [
          '[&_td:nth-of-type(3),&_th:nth-of-type(3)]:sticky',
          '[&_td:nth-of-type(3),&_th:nth-of-type(3)]:left-(--sticky-offset-left-3)',
          '[&_td:nth-of-type(3),&_th:nth-of-type(3)]:min-w-(--sticky-column-width-3)',
          '[&_td:nth-of-type(3),&_th:nth-of-type(3)]:z-2'
        ],
        numberOfStickyColumns >= 4 && [
          '[&_td:nth-of-type(4),&_th:nth-of-type(4)]:sticky',
          '[&_td:nth-of-type(4),&_th:nth-of-type(4)]:left-(--sticky-offset-left-4)',
          '[&_td:nth-of-type(4),&_th:nth-of-type(4)]:min-w-(--sticky-column-width-4)',
          '[&_td:nth-of-type(4),&_th:nth-of-type(4)]:z-2'
        ],
        numberOfStickyColumns === 1 &&
          hasScroll && [
            '[&_td:nth-of-type(1),&_th:nth-of-type(1)]:shadow-[var(--color-alpha-200)_-2px_-3px_9px_1px]',
            '[&_td:nth-of-type(1),&_th:nth-of-type(1)]:[clip-path:inset(-1px_-10px_-1px_0px)]'
          ],
        numberOfStickyColumns === 2 &&
          hasScroll && [
            '[&_td:nth-of-type(2),&_th:nth-of-type(2)]:shadow-[var(--color-alpha-200)_-2px_-3px_9px_1px]',
            '[&_td:nth-of-type(2),&_th:nth-of-type(2)]:[clip-path:inset(-1px_-10px_-1px_0px)]'
          ],
        numberOfStickyColumns === 3 &&
          hasScroll && [
            '[&_td:nth-of-type(3),&_th:nth-of-type(3)]:shadow-[var(--color-alpha-200)_-2px_-3px_9px_1px]',
            '[&_td:nth-of-type(3),&_th:nth-of-type(3)]:[clip-path:inset(-1px_-10px_-1px_0px)]'
          ],
        numberOfStickyColumns === 4 &&
          hasScroll && [
            '[&_td:nth-of-type(4),&_th:nth-of-type(4)]:shadow-[var(--color-alpha-200)_-2px_-3px_9px_1px]',
            '[&_td:nth-of-type(4),&_th:nth-of-type(4)]:[clip-path:inset(-1px_-10px_-1px_0px)]'
          ]
      )}
      {...restProps}
    >
      {children}
    </div>
  )
}
